// @ts-nocheck
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Channel } from '@src/models/channel';
import { Tag, Icon } from '@workspace/design-system/src/components';
import { useGetPlanOrders } from '@src/apis/pay/queries';
import { useChannelPagination } from '@src/hooks/channel/use-channel-pagination';
import { getPlanLabel } from '../constants';
import type { OrderStatus, PaymentChannel, ApplicationOrderResponseDto } from '@src/apis/pay';
import { formatPhoneNumberForDisplay } from '@src/utils/phone';

type PaymentStatus = '완료' | '취소' | '실패';
type PaymentProduct = '무료' | '프로' | '엔터프라이즈' | '티처' | '클래스' | '리얼월드';
type PaymentMethod = '카드 결제' | '세금계산서';

interface ChannelPaymentTabProps {
  channel: Channel;
}

export function ChannelPaymentTab({ channel }: ChannelPaymentTabProps) {
  // 페이지네이션
  const pagination = useChannelPagination({
    initialPage: 1,
    initialLimit: 10,
    initialTotal: 0,
  });

  // API 호출
  const { data: planOrdersResponse, isLoading: isLoadingPaymentHistories } = useGetPlanOrders({
    applicationId: channel.channelId,
    page: pagination.currentPage - 1, // API는 0-based index 사용
    size: pagination.limit,
  });

  // 페이지네이션 total 업데이트
  useEffect(() => {
    const paginatedData = planOrdersResponse?.data?.data;
    
    if (paginatedData) {
      pagination.setTotal(paginatedData.totalElements);
    }
  }, [planOrdersResponse, pagination]);

  // API 응답 데이터
  const paymentHistory = useMemo<ApplicationOrderResponseDto[]>(() => {
    const paginatedData = planOrdersResponse?.data?.data;
    const ordersArray = paginatedData?.content;
    
    if (!ordersArray || !Array.isArray(ordersArray)) {
      return [];
    }

    return ordersArray;
  }, [planOrdersResponse]);

  // 스크롤 관련 상태와 refs
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // 날짜 포맷팅 함수
  const formatDate = useCallback((dateString: string | undefined, paymentMethod?: PaymentMethod): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());

    // 세금계산서인 경우 일자까지만 표시
    if (paymentMethod === '세금계산서') {
      return `${year}-${month}-${day}`;
    }

    // 나머지는 초까지 표시
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }, []);

  // 금액 포맷팅 함수
  const formatAmount = useCallback((amount: number): string => {
    if (amount === undefined || amount === null) return '0원';
    return `${amount.toLocaleString()}원`;
  }, []);

  // 결제 상태 변환 및 태그 스타일 매핑
  const getStatusInfo = useCallback((orderStatus: OrderStatus) => {
    let statusText: PaymentStatus = '완료';
    let color = 'green' as const;

    if (orderStatus === 'Canceled' || orderStatus === 'Refunded') {
      statusText = '취소';
      color = 'gray';
    } else if (orderStatus === 'Failed') {
      statusText = '실패';
      color = 'red';
    } else if (orderStatus === 'Paid' || orderStatus === 'Created') {
      statusText = '완료';
      color = 'green';
    }

    return { text: statusText, color };
  }, []);

  // 결제 방식 변환
  const getPaymentMethod = useCallback((paymentChannel: PaymentChannel): PaymentMethod => {
    return paymentChannel === 'Invoice' ? '세금계산서' : '카드 결제';
  }, []);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      setIsScrolled(scrollTop > 0);
      setShowBottomShadow(scrollTop + clientHeight < scrollHeight);
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      // 초기 상태 확인
      handleScroll();
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [paymentHistory]); // 데이터 변경 시 스크롤 상태 재확인

  // 영수증 다운로드
  const handleReceiptDownload = useCallback((receiptUrl: string) => {
    window.open(receiptUrl, '_blank');
  }, []);

  return (
    <div>
      <div className="relative">
        {/* 스크롤 시 상단 그림자 */}
        {isScrolled && (
          <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-[#000000]/10 to-transparent z-10 pointer-events-none" />
        )}

        {/* 테이블 컨테이너 */}
        <div
          ref={contentRef}
          className="overflow-auto max-h-[500px]"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#D9D9D9 transparent' }}
        >
          <table className="w-full border-collapse min-w-[1600px]">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="bg-[#F5F5F5]">
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 상태</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 일시</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 상품</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 방식</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">영수증</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 금액</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 요청 ID</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제자 명</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제자 연락처</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제자 이메일</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 취소 일시</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingPaymentHistories ? (
                <tr>
                  <td colSpan={11} className="p-3 text-center text-[#BFBFBF]">
                    로딩 중...
                  </td>
                </tr>
              ) : paymentHistory.length === 0 ? (
                <tr>
                  <td colSpan={11} className="p-3 text-center text-[#BFBFBF]">
                    결제 내역이 없습니다.
                  </td>
                </tr>
              ) : (
                paymentHistory.map((item) => {
                  const statusInfo = getStatusInfo(item.status);
                  const paymentMethod = getPaymentMethod(item.paymentChannel);
                  const productLabel = getPlanLabel(item.productName);

                  return (
                  <tr key={item.id} className="border-b border-[#F0F0F0] hover:bg-[#F5F5F5] transition-colors">
                    {/* 결제 상태 */}
                    <td className="p-3 whitespace-nowrap">
                      <Tag
                        color={statusInfo.color}
                        size="S-16"
                        text={statusInfo.text}
                        rounded
                      />
                    </td>

                    {/* 결제 일시 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {formatDate(item.createdAt, paymentMethod)}
                    </td>

                    {/* 결제 상품 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {productLabel}
                    </td>

                    {/* 결제 방식 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {paymentMethod}
                    </td>

                    {/* 영수증 */}
                    <td className="p-3 whitespace-nowrap">
                      {item.receiptUrl ? (
                        <button
                          onClick={() => handleReceiptDownload(item.receiptUrl!)}
                          className="text-[14px] text-[#0066FF] leading-[20px] tracking-[-0.1px] hover:underline cursor-pointer"
                          aria-label="영수증 다운로드"
                        >
                          영수증
                        </button>
                      ) : (
                        <span className="text-[14px] text-[#BFBFBF] leading-[20px] tracking-[-0.1px]">-</span>
                      )}
                    </td>

                    {/* 결제 금액 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {formatAmount(item.price)}
                    </td>

                    {/* 결제 요청 ID */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {item.paymentRequestId || '-'}
                    </td>

                    {/* 결제자명 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {item.payerName || '-'}
                    </td>

                    {/* 결제자 연락처 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {formatPhoneNumberForDisplay(item.payerPhone)}
                    </td>

                    {/* 결제자 이메일 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {item.payerEmail || '-'}
                    </td>

                    {/* 결제 취소 일시 */}
                    <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                      {formatDate(item.canceledAt)}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 스크롤 시 하단 그라데이션 그림자 */}
        {showBottomShadow && (
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" />
        )}
      </div>

      {/* 페이지네이션 */}
      {!isLoadingPaymentHistories && paymentHistory.length > 0 && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6 py-4">
          <button
            onClick={pagination.handlePrevPage}
            disabled={pagination.currentPage === 1}
            className="p-2 text-[#595959] hover:text-[#101010] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronLeft" size={16} color={pagination.currentPage === 1 ? '#BFBFBF' : '#595959'} />
          </button>

          {(() => {
            const pages: (number | string)[] = [];
            const siblingCount = 2;
            const totalPageNumbers = siblingCount * 2 + 1;

            if (pagination.totalPages <= totalPageNumbers + 2) {
              for (let i = 1; i <= pagination.totalPages; i++) {
                pages.push(i);
              }
            } else {
              pages.push(1);

              let startPage = Math.max(2, pagination.currentPage - siblingCount);
              let endPage = Math.min(pagination.totalPages - 1, pagination.currentPage + siblingCount);

              if (pagination.currentPage <= siblingCount + 1) {
                endPage = Math.min(totalPageNumbers, pagination.totalPages - 1);
              }

              if (pagination.currentPage >= pagination.totalPages - siblingCount) {
                startPage = Math.max(2, pagination.totalPages - totalPageNumbers);
              }

              const showLeftEllipsis = startPage > 2;
              if (showLeftEllipsis) {
                pages.push('left-ellipsis');
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }

              const showRightEllipsis = endPage < pagination.totalPages - 1;
              if (showRightEllipsis) {
                pages.push('right-ellipsis');
              }

              pages.push(pagination.totalPages);
            }

            return pages.map((page) => {
              if (typeof page === 'string') {
                return (
                  <div key={page} className="flex items-center justify-center w-6 h-6">
                    <Icon name="Meatball" size={24} color="#595959" />
                  </div>
                );
              }

              const isActive = page === pagination.currentPage;
              return (
                <button
                  key={page}
                  onClick={() => pagination.goToPage(page)}
                  className={`w-6 h-6 rounded text-[14px] font-bold leading-[16px] ${isActive ? 'bg-[#E6F4FF] text-[#3C78F4]' : 'text-[#595959] hover:text-[#101010]'
                    } transition-colors`}
                >
                  {page}
                </button>
              );
            });
          })()}

          <button
            onClick={pagination.handleNextPage}
            disabled={pagination.currentPage === pagination.totalPages}
            className="p-2 text-[#595959] hover:text-[#101010] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Icon
              name="ChevronRight"
              size={16}
              color={pagination.currentPage === pagination.totalPages ? '#BFBFBF' : '#595959'}
            />
          </button>
        </div>
      )}
    </div>
  );
}

